import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import arJson from "@/i18n/ar.json";
import enJson from "@/i18n/en.json";

const dictionaries = { ar: arJson, en: enJson };
const SUPPORTED = ["ar", "en"];
const DEFAULT_LANG = "ar";

const I18nContext = createContext({ lang: DEFAULT_LANG, t: (k) => k });

function getFromPath(obj, path) {
  if (!path) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

export function getLangFromPathname(pathname) {
  const first = (pathname || "/").split("/").filter(Boolean)[0];
  if (SUPPORTED.includes(first)) return first;
  return DEFAULT_LANG;
}

export function I18nProvider({ children, forceLang }) {
  const location = useLocation();
  const lang = forceLang || getLangFromPathname(location.pathname);
  const dict = dictionaries[lang] || dictionaries[DEFAULT_LANG];

  const t = useCallback(
    (key, fallback) => {
      const val = getFromPath(dict, key);
      if (val === undefined || val === null) return fallback ?? key;
      return val;
    },
    [dict],
  );

  const value = useMemo(() => ({ lang, t, dict, supported: SUPPORTED }), [lang, t, dict]);

  React.useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang === "ar" ? "es-AR" : "en";
    }
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}

export { SUPPORTED, DEFAULT_LANG };
